import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { CompanyDetailsDTOV1 } from '@/common/types/company'
import { getCompany, getCompanyCases, getCompanyCustomers } from '@/common/services/CompanyService'
import { publish } from '@/common/services/eventBusService'
import { unlinkCustomerFromCompany } from '@/common/services/CustomerService'
import { CompanyExpandEnum } from '@/common/types/company'
import { getCaseCompanies } from '@/common/services/CaseService'
import { useSessionStore } from '@/common/stores/sessionStore'
import { AuthoritiesEnum } from '@/common/types/session'
import type { Case } from '@/common/types/case'
import type { Customer } from '@/common/types/customer'
import { EntityEnum } from '@/common/types/general'
import { fetchEntities, setColumnsStatus } from '@/search/services/SearchFetchService'
import { getResultViewConfigToDisplay } from '@/search/services/SearchService'
import { SearchStoreIdEnum } from '@/search/types/search'
import { ColumnStatusEnum, type ResultViewConfig } from '@/search/types/resultViewConfig'

export const useCompanyStore = defineStore('company', () => {
  const currentCompany = ref<CompanyDetailsDTOV1 | null>(null)
  const companyId = ref<number | undefined>(undefined)
  const companyCases = ref<Case[]>([])
  const casesResultViewConfig = ref<ResultViewConfig | null>(null)
  const companyCustomers = ref<Customer[]>([])
  const customersResultViewConfig = ref<ResultViewConfig | null>(null)

  const setCompanyName = (name: string | undefined) => {
    if (!currentCompany.value) return
    if (name && name.length > 0) {
      currentCompany.value.name = name
    } else {
      currentCompany.value.name = undefined
    }
  }

  const setCurrentCompany = (company: CompanyDetailsDTOV1 | null) => {
    currentCompany.value = company
  }

  const setCompanyId = (id: number | undefined) => {
    companyId.value = id
  }

  const fetchCompanyById = async (id: number) => {
    const company = await getCompany(id, {
      expands: new Set([CompanyExpandEnum.CUSTOM_FIELDS]),
    })
    currentCompany.value = company
    return company
  }

  const loadCompanyIdForCase = async (caseId: number) => {
    const sessionStore = useSessionStore()
    if (sessionStore.getAuthoritySet.has(AuthoritiesEnum.COMPANY_SEARCH)) {
      const companyIds = await getCaseCompanies(caseId)
      companyId.value = companyIds.length > 0 ? companyIds[0] : undefined
    } else {
      companyId.value = undefined
    }
  }

  const loadCasesForCompany = async (companyIdParam: number) => {
    companyCases.value = []
    casesResultViewConfig.value = null

    const caseIds = await getCompanyCases(companyIdParam)
    if (caseIds && caseIds.length > 0) {
      const resultViewConfig = await getResultViewConfigToDisplay(SearchStoreIdEnum.MAIN, EntityEnum.TICKET)
      const visibleColumns = resultViewConfig.columns.filter((column) => column.alwaysVisible === true)
      casesResultViewConfig.value = {
        ...resultViewConfig,
        columns: visibleColumns,
      }
      const columnsDisplayed = casesResultViewConfig.value.columns
      setColumnsStatus(columnsDisplayed, ColumnStatusEnum.LOADING)
      companyCases.value = (await fetchEntities(EntityEnum.TICKET, caseIds, columnsDisplayed)) as Case[]
    }
  }

  const loadCustomersForCompany = async (companyIdParam: number) => {
    companyCustomers.value = []
    customersResultViewConfig.value = null

    const customerIds = await getCompanyCustomers(companyIdParam)
    if (customerIds && customerIds.length > 0) {
      const resultViewConfig = await getResultViewConfigToDisplay(SearchStoreIdEnum.MAIN, EntityEnum.CUSTOMER)
      const visibleColumns = resultViewConfig.columns.filter((column) => column.alwaysVisible === true)
      customersResultViewConfig.value = {
        ...resultViewConfig,
        columns: visibleColumns,
      }
      const columnsDisplayed = customersResultViewConfig.value.columns
      setColumnsStatus(columnsDisplayed, ColumnStatusEnum.LOADING)
      companyCustomers.value = (await fetchEntities(EntityEnum.CUSTOMER, customerIds, columnsDisplayed)) as Customer[]
    }
  }

  const unlinkCustomer = async (customerId: number) => {
    await unlinkCustomerFromCompany(customerId)
    companyCustomers.value = companyCustomers.value.filter((c) => c.id !== customerId)
    publish('global:legacy-frame-updated')
  }

  const clearCompany = () => {
    setCompanyName(undefined)
    companyId.value = undefined
    companyCases.value = []
    companyCustomers.value = []
  }

  return {
    currentCompany,
    companyId,
    companyCases,
    casesResultViewConfig,
    companyCustomers,
    customersResultViewConfig,
    setCompanyName,
    setCurrentCompany,
    setCompanyId,
    fetchCompanyById,
    loadCompanyIdForCase,
    loadCasesForCompany,
    loadCustomersForCompany,
    unlinkCustomer,
    clearCompany,
  }
})
